/**
 * Export Utilities
 *
 * Functions for exporting data to various formats (JSON, CSV, PDF)
 */

/**
 * Download data as JSON file
 */
export function exportToJSON(data: unknown, filename: string): void {
  const jsonString = JSON.stringify(data, null, 2)
  const blob = new Blob([jsonString], { type: 'application/json' })
  downloadBlob(blob, `${filename}.json`)
}

/**
 * Convert array of objects to CSV format
 */
export function arrayToCSV<T extends Record<string, unknown>>(
  data: T[],
  headers?: string[]
): string {
  if (data.length === 0) {
    return ''
  }

  // Use provided headers or extract from first object
  const cols = headers || Object.keys(data[0])

  // Create header row
  const headerRow = cols.map(escapeCSVValue).join(',')

  // Create data rows
  const dataRows = data.map(row =>
    cols.map(col => {
      const value = row[col]
      return escapeCSVValue(value)
    }).join(',')
  )

  return [headerRow, ...dataRows].join('\n')
}

/**
 * Download data as CSV file
 */
export function exportToCSV<T extends Record<string, unknown>>(
  data: T[],
  filename: string,
  headers?: string[]
): void {
  const csvContent = arrayToCSV(data, headers)
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  downloadBlob(blob, `${filename}.csv`)
}

/**
 * Escape CSV values (handle commas, quotes, newlines)
 */
function escapeCSVValue(value: unknown): string {
  if (value === null || value === undefined) {
    return ''
  }

  const stringValue = String(value)

  // If value contains comma, quote, or newline, wrap in quotes and escape quotes
  if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
    return `"${stringValue.replace(/"/g, '""')}"`
  }

  return stringValue
}

/**
 * Download blob as file
 */
function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/**
 * Format date for export filenames
 */
export function getExportTimestamp(): string {
  const now = new Date()
  return now.toISOString().replace(/[:.]/g, '-').slice(0, -5)
}

/**
 * Export analytics data with formatted structure
 */
export interface AnalyticsExport {
  exportDate: string
  summary: {
    totalTasks: number
    successRate: number
    trend: string
  }
  periods: Array<{
    date: string
    successRate: number
    total: number
  }>
  resources?: Array<{
    name: string
    status: string
    daysProtected: number
    successRate: number
  }>
}

export function exportAnalyticsReport(
  data: AnalyticsExport,
  format: 'json' | 'csv' = 'json'
): void {
  const timestamp = getExportTimestamp()
  const filename = `analytics-report-${timestamp}`

  if (format === 'json') {
    exportToJSON(data, filename)
  } else {
    // Export periods as CSV
    if (data.periods && data.periods.length > 0) {
      exportToCSV(data.periods, `${filename}-periods`)
    }

    // Export resources as CSV if available
    if (data.resources && data.resources.length > 0) {
      exportToCSV(data.resources, `${filename}-resources`)
    }
  }
}

/**
 * Export compliance report
 */
export interface ComplianceExport {
  exportDate: string
  overall: {
    slaMet: boolean
    successRate: number
    threshold: number
    complianceRate: number
  }
  breaches: Array<{
    date: string
    successRate: number
    gap: number
    failedCount: number
    totalCount: number
  }>
  periods: Array<{
    date: string
    successRate: number
    slaMet: boolean
  }>
}

export function exportComplianceReport(
  data: ComplianceExport,
  format: 'json' | 'csv' = 'json'
): void {
  const timestamp = getExportTimestamp()
  const filename = `compliance-report-${timestamp}`

  if (format === 'json') {
    exportToJSON(data, filename)
  } else {
    // Export breaches
    if (data.breaches && data.breaches.length > 0) {
      exportToCSV(data.breaches, `${filename}-breaches`)
    }

    // Export periods
    if (data.periods && data.periods.length > 0) {
      exportToCSV(data.periods, `${filename}-periods`)
    }
  }
}

/**
 * Export resource timeline
 */
export interface TimelineExport {
  exportDate: string
  resources: Array<{
    resourceId: string
    resourceName: string
    status: string
    daysProtected: number
    daysSinceLastBackup: number
    totalBackups: number
    successfulBackups: number
    failedBackups: number
    successRate: number
    avgFrequencyDays: number
    reason: string
  }>
}

export function exportResourceTimeline(
  data: TimelineExport,
  format: 'json' | 'csv' = 'json'
): void {
  const timestamp = getExportTimestamp()
  const filename = `resource-timeline-${timestamp}`

  if (format === 'json') {
    exportToJSON(data, filename)
  } else {
    exportToCSV(data.resources, filename)
  }
}

/**
 * Copy data to clipboard as JSON
 */
export async function copyToClipboard(data: unknown): Promise<void> {
  const jsonString = JSON.stringify(data, null, 2)
  await navigator.clipboard.writeText(jsonString)
}

/**
 * Print report (opens browser print dialog)
 */
export function printReport(): void {
  window.print()
}

/**
 * Generate filename with timestamp
 */
export function generateFilename(base: string, extension: string): string {
  const timestamp = getExportTimestamp()
  return `${base}-${timestamp}.${extension}`
}
